// src/components/cctv/CameraView.js
import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft } from "lucide-react";
import { Capacitor } from "@capacitor/core";

const ORIENTATION_RELOAD_DELAY_MS = 400;

// Minimum virtual width the vendor iframe needs to keep
// its camera-selection sidebar + main feed side-by-side.
// We give the iframe this width, then CSS-scale it so the
// display fills 100% of the device width. Height follows the
// 16:9 ratio and is always shorter than (never overflows) the container.
const VENDOR_VIRTUAL_W = 800;
const VENDOR_VIRTUAL_H = 450; // 16:9

const getScreenOrientation = () =>
    window.innerWidth > window.innerHeight ? "landscape" : "portrait";

const CameraView = ({
    onBack,
    streamUrl,
    useLegacyPlayer = false,
    usePostEmbed = false,
    embedUrl,
    embedParams,
    hybridAuth = { enabled: false },
}) => {
    console.log("st", streamUrl)

    const isM3U8 = useLegacyPlayer && streamUrl?.includes(".m3u8");
    const isNativeApp = Capacitor.isNativePlatform();
    const useLandscapeScaling = isNativeApp;

    const formRef = useRef(null);
    const iframeRef = useRef(null);
    const containerRef = useRef(null);

    const [playerKey, setPlayerKey] = useState(0);
    const [isOrientationReloading, setIsOrientationReloading] = useState(false);
    const [orientation, setOrientation] = useState(getScreenOrientation());
    const orientationRef = useRef(getScreenOrientation());
    const [landscapeStyle, setLandscapeStyle] = useState({ scale: 1, height: 0 });

    useEffect(() => {
        if (!isNativeApp) return;

        const handleOrientationChange = () => {
            const nextOrientation = getScreenOrientation();
            if (nextOrientation === orientationRef.current) return;
            orientationRef.current = nextOrientation;

            setOrientation(nextOrientation);
            setIsOrientationReloading(true);
            window.setTimeout(() => {
                setPlayerKey((key) => key + 1);
                setIsOrientationReloading(false);
            }, ORIENTATION_RELOAD_DELAY_MS);
        };

        window.addEventListener("orientationchange", handleOrientationChange);
        window.addEventListener("resize", handleOrientationChange);

        return () => {
            window.removeEventListener("orientationchange", handleOrientationChange);
            window.removeEventListener("resize", handleOrientationChange);
        };
    }, [isNativeApp]);

    // In landscape (native app only): scale based on width so display always fills 100% device width.
    useEffect(() => {
        if (!useLandscapeScaling || orientation !== "landscape" || isOrientationReloading) return;

        const compute = () => {
            if (!containerRef.current) return;
            const { offsetWidth: w } = containerRef.current;
            const scale = w / VENDOR_VIRTUAL_W;
            setLandscapeStyle({ scale, height: VENDOR_VIRTUAL_H * scale });
        };

        compute();

        const ro = new ResizeObserver(compute);
        if (containerRef.current) ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, [useLandscapeScaling, orientation, isOrientationReloading, playerKey]);

    // Auto-submit hidden form whenever we have valid embed params
    useEffect(() => {
        if (useLegacyPlayer) return;
        if (
            !usePostEmbed ||
            !embedUrl ||
            !embedParams?.id1 ||
            !embedParams?.id2 ||
            !embedParams?.hash ||
            (hybridAuth?.enabled && hybridAuth?.hasError !== true)
        ) {
            return;
        }

        try {
            if (formRef.current) {
                formRef.current.submit();
            }
        } catch (e) {
            console.error("[CameraView] Failed to submit CCTV embed form", e);
        }
    }, [
        usePostEmbed,
        embedUrl,
        embedParams?.id1,
        embedParams?.id2,
        embedParams?.hash,
        hybridAuth?.enabled,
        hybridAuth?.hasError,
        playerKey,
    ]);

    const renderPlayer = () => {
        if (isM3U8) {
            return (
                <video
                    src={streamUrl}
                    autoPlay
                    controls
                    playsInline
                    className="w-full h-full object-contain"
                />
            );
        }

        if (hybridAuth?.enabled && hybridAuth?.src) {
            return (
                <iframe
                    title="cctv-hybrid"
                    src={hybridAuth.src}
                    className="w-full h-full"
                    allowFullScreen
                    frameBorder="0"
                />
            );
        }

        if (useLegacyPlayer) {
            return (
                <iframe
                    title="cctv"
                    src={streamUrl}
                    className="w-full h-full"
                    allowFullScreen
                    frameBorder="0"
                    referrerpolicy="no-referrer-when-downgrade"
                />
            );
        }

        if (hybridAuth?.enabled && hybridAuth?.hasError !== true && !hybridAuth?.src) {
            return <div className="text-white">Loading camera...</div>;
        }

        if (
            usePostEmbed &&
            embedUrl &&
            embedParams?.id1 &&
            embedParams?.id2 &&
            embedParams?.hash
        ) {
            return (
                <div className="w-full h-full">
                    <iframe
                        name="center-embed"
                        id="center-embed"
                        ref={iframeRef}
                        width="100%"
                        height="100%"
                        frameBorder="0"
                        allow="fullscreen"
                        className="w-full h-full"
                        title="cctv-embed"
                    />

                    <form
                        ref={formRef}
                        method="POST"
                        action={embedUrl}
                        target="center-embed"
                        style={{ display: "none" }}
                    >
                        <input type="hidden" name="id1" value={embedParams.id1} />
                        <input type="hidden" name="id2" value={embedParams.id2} />
                        <input type="hidden" name="hash" value={embedParams.hash} />
                    </form>
                </div>
            );
        }

        return (
            <iframe
                title="cctv"
                src={streamUrl}
                className="w-full h-full"
                allowFullScreen
                frameBorder="0"
            />
        );
    };

    return (
        <div className="fixed inset-0 bg-black flex flex-col z-50">

            {/* HEADER */}
            <div className="flex items-center justify-between p-4 bg-gray-900">
                <button onClick={onBack} className="text-white">
                    <ChevronLeft className="w-6 h-6" />
                </button>
                <h2 className="text-white font-semibold cursor-pointer" onClick={onBack}>Live Camera</h2>
                <div className="w-6"></div>
            </div>

            {/* PLAYER */}
            <div
                ref={containerRef}
                className={`flex-1 bg-black overflow-hidden flex ${
                    isOrientationReloading ||
                    !useLandscapeScaling ||
                    orientation !== "landscape"
                        ? "items-center justify-center"
                        : "items-start justify-start"
                }`}
            >
                {isOrientationReloading ? (
                    <div className="text-white text-center">
                        <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-3" />
                        <p>Loading camera...</p>
                    </div>
                ) : useLandscapeScaling && orientation === "landscape" ? (
                    // Landscape: fixed virtual width so vendor layout stays side-by-side,
                    // CSS-scaled to fill 100% of device width. Height follows proportionally.
                    <div
                        key={playerKey}
                        style={{
                            width: VENDOR_VIRTUAL_W,
                            height: VENDOR_VIRTUAL_H,
                            transform: `scale(${landscapeStyle.scale})`,
                            transformOrigin: "top left",
                            flexShrink: 0,
                        }}
                    >
                        {renderPlayer()}
                    </div>
                ) : (
                    <div key={playerKey} className="w-full h-full">
                        {renderPlayer()}
                    </div>
                )}
            </div>

        </div>
    );
};

export default CameraView;
